import { useMutation, useQuery, type MutationOptions, type UseQueryOptions } from "@tanstack/react-query";
import api from "../utils/api";
import { getURLPrefix } from "../utils/getUrlPrefix";
import { logoutUser } from "../redux/reducers/userReducer";
import { setTokens } from "../redux/reducers/authReducers";
import { store, type AppStore } from "../redux/store";
import axios, { type AxiosResponse } from "axios";
import { useAppStore } from "../redux/hooks";
import { tokenSchema } from "../modules/auth/tokens/tokenSchema";
import { getAuthTokens, setAuthTokens } from "../modules/auth/tokens/tokens";
import { GV_API_AUTH_CLIENT_SECRET, PRODUCTION_GV_API_AUTH_CLIENT_ID } from "../utils/gvSecrets";
import { isNonRetryableClientError } from "../utils/isNonRetryableClientError";

const API_URL = getURLPrefix();

export const sleep = async (ms: number) => await new Promise<void>((r) => setTimeout(r, ms));

let queue: { resolve: () => void; reject: () => void }[] = [];
let isRefreshing = false;

class AuthException extends Error {
  sourceError?: Response;
  constructor(message: string, error?: Response) {
    super(message);
    this.message = message;
    this.sourceError = error;
    this.name = "AuthException";
  }
}

const refreshTokenQueue = async () => {
  if (window.location.href.includes("/pdf/")) {
    return;
  }

  // oxlint-disable-next-line no-async-promise-executor
  await new Promise<void>(async (resolve, reject) => {
    if (isRefreshing) {
      queue.push({ resolve, reject });
    } else {
      isRefreshing = true;
      await refreshAuthToken()
        .then(() => {
          isRefreshing = false;
          resolve();
          for (const queued of queue) {
            queued.resolve();
          }
          queue = [];
        })
        .catch((error: Error) => {
          isRefreshing = false;
          reject(error);
          for (const queued of queue) {
            queued.reject();
          }
          queue = [];
        });
    }
  });
};

const refreshAuthToken = async () => {
  const oAuthTokens = getAuthTokens();
  if (!oAuthTokens) {
    store.dispatch(setAuthTokens(null));
    throw new AuthException("failed to refresh token");
  }
  const authData = {
    client_id: PRODUCTION_GV_API_AUTH_CLIENT_ID,
    client_secret: GV_API_AUTH_CLIENT_SECRET,
    grant_type: "refresh_token",
    refresh_token: oAuthTokens.refresh_token,
  };
  const authResp = await fetch(`${API_URL}/auth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(authData),
  });
  if (authResp.ok) {
    const oAuthTokens = await authResp.json().then((r) => tokenSchema.parse(r));
    store.dispatch(setAuthTokens(oAuthTokens));
  } else {
    store.dispatch(setAuthTokens(null));
    throw new AuthException("failed to refresh token", authResp);
  }
};

const apiGet = async <T>(
  endpoint: string,
  params: unknown,
  store: AppStore,
  retries = 0,
): Promise<AxiosResponse<T>> => {
  try {
    const resp = await api.get<T>(endpoint, params);
    return resp;
  } catch (error) {
    if (retries < 3) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        try {
          await refreshTokenQueue();
          // Update Redux with new tokens for legacy code
          const tokens = getAuthTokens();
          store.dispatch(setTokens(tokens));
        } catch (error) {
          if (error instanceof Error && error.name === "AuthException") {
            // logout user if refresh token fails
            store.dispatch(logoutUser());
          }
          throw error;
        }
      } else if (isNonRetryableClientError(error)) {
        throw error;
      } else {
        await sleep(500);
      }
      return await apiGet<T>(endpoint, params, store, retries + 1);
    }
    throw error;
  }
};

const useFetch = <TQueryFnData, TError = unknown, TData = TQueryFnData>(
  queryKey: unknown[],
  endpoint: string,
  params: unknown,
  options: Omit<UseQueryOptions<AxiosResponse<TQueryFnData>, TError, TData>, "queryKey" | "queryFn">,
) => {
  const store = useAppStore();
  return useQuery<AxiosResponse<TQueryFnData>, TError, TData>({
    queryKey: [...queryKey, { endpoint, params }],
    queryFn: async () => await apiGet<TQueryFnData>(endpoint, params, store),
    ...options,
  });
};

const useCreate = (endpoint: string, params: unknown, options: MutationOptions) =>
  useMutation({ mutationFn: async (body) => await api.post(endpoint, body, params), ...options });

const useUpdate = (endpoint: string, params: unknown, options: MutationOptions) =>
  useMutation({ mutationFn: async (body) => await api.patch(endpoint, body, params), ...options });

export { useFetch, useCreate, useUpdate };
